import { Dialog } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import ConfettiLayer from "./lotto-loco/confetti";
import { ensureKeyframes } from "./lotto-loco/keyframes";
import { Backdrop, ModalShell } from "./lotto-loco/modal.styles";
import { pickRandomBeers } from "./lotto-loco/random";
import ReelRow, { Phase } from "./lotto-loco/reel-row";

export type LottoLocoModalProps = {
  open: boolean;
  rounds: number;
  beers: string[];
  onCancel: () => void;
  onConfirm: (picks: (string | null)[]) => void;
};

const SPIN_DURATION_MS = 2200;

const LottoLocoModal = ({
  open,
  rounds,
  beers,
  onCancel,
  onConfirm,
}: LottoLocoModalProps) => {
  const [phase, setPhase] = useState<Phase>("ready");
  const [picks, setPicks] = useState<(string | null)[]>([]);

  useEffect(() => {
    ensureKeyframes();
  }, []);

  // Reset to "ready" each time the modal is reopened so a previous reveal
  // does not flash before the user spins again.
  useEffect(() => {
    if (open) {
      setPhase("ready");
      setPicks([]);
    }
  }, [open]);

  const spin = () => {
    const nextPicks = pickRandomBeers(rounds, beers);
    setPicks(nextPicks);
    setPhase("spinning");
    setTimeout(() => setPhase("reveal"), SPIN_DURATION_MS);
  };

  // Cancel-during-spin is a deliberate no-op so the user doesn't accidentally
  // dismiss mid-animation. The footer already hides the cancel button while
  // spinning; this guards the backdrop click too.
  const handleCancel = () => {
    if (phase === "spinning") return;
    onCancel();
  };

  const handleConfirm = () => {
    onConfirm(picks);
  };

  // Loop the strip twice so the linear scroll animation can wrap seamlessly.
  const reelStrip = useMemo(() => [...beers, ...beers], [beers]);

  const visibleRows = phase === "ready" ? Math.min(rounds, 5) : rounds;
  const hiddenCount = phase === "ready" ? Math.max(0, rounds - visibleRows) : 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      PaperProps={{
        style: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      hideBackdrop
    >
      <Backdrop onClick={handleCancel}>
        <ModalShell
          data-testid="lotto-loco-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {phase === "reveal" && <ConfettiLayer />}

          <div className="header">
            <div className="header-title">
              <span
                className={`slot-icon${
                  phase === "spinning" ? " slot-icon--spinning" : ""
                }`}
                aria-hidden
              >
                🎰
              </span>
              <span className="header-name">LOTTO LOCO</span>
            </div>
            <div className="header-sub">
              {phase === "ready" &&
                "Spin to randomly fill all your rounds. See if luck wins."}
              {phase === "spinning" && "Spinning the reels…"}
              {phase === "reveal" && "Your lotto picks are in. Locked in?"}
            </div>
          </div>

          <div className={`reels reels--${phase}`}>
            {Array.from({ length: visibleRows }).map((_, i) => (
              <ReelRow
                key={i}
                rowNumber={i + 1}
                rowIndex={i}
                phase={phase}
                pick={picks[i] ?? null}
                reelStrip={reelStrip}
              />
            ))}
            {hiddenCount > 0 && (
              <div className="more-rounds">+ {hiddenCount} MORE ROUNDS</div>
            )}
          </div>

          <div className="footer">
            {phase === "ready" && (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={spin}
                  data-testid="lotto-loco-spin"
                >
                  🎲 Spin the reels
                </button>
                <button type="button" className="btn-link" onClick={handleCancel}>
                  Cancel
                </button>
              </>
            )}
            {phase === "spinning" && (
              <div className="rolling">✦ ROLLING ✦</div>
            )}
            {phase === "reveal" && (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirm}
                  data-testid="lotto-loco-confirm"
                >
                  Lock In My Lotto
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={spin}
                  data-testid="lotto-loco-spin-again"
                >
                  ↻ Spin Again
                </button>
                <button type="button" className="btn-link" onClick={handleCancel}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </ModalShell>
      </Backdrop>
    </Dialog>
  );
};

export default LottoLocoModal;
